import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Shield,
  HardDrive,
  Save,
  Lock,
  Eye,
  EyeOff,
  FileText,
  LayoutDashboard,
  Edit2,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2
} from 'lucide-react';
import SidebarLayout from '../components/SidebarLayout';
import { useAuth } from '../utils/AuthContext';
import authService from '../services/authService';
import fileService from '../services/fileService';
import { formatFileSize } from '../utils/helpers';
import { cn } from '../lib/utils';

// Shadcn UI Components
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar"
import { Badge } from "../components/ui/badge"
import { Progress } from "../components/ui/progress"
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog"

const Profile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile data
  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    role: '',
    account_status: '',
    storage_limit: 0,
    created_at: ''
  });

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({
    username: '',
    email: ''
  });

  // Storage info
  const [storageInfo, setStorageInfo] = useState(null);

  // Change password dialog
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    loadProfileData();
    loadStorageInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      const response = await authService.getCurrentUser();

      if (response.success && response.user) {
        setProfileData(response.user);
        setEditedData({
          username: response.user.username,
          email: response.user.email
        });
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Gagal memuat data profil');
    } finally {
      setLoading(false);
    }
  };

  const loadStorageInfo = async () => {
    try {
      const response = await fileService.getStorageStats();
      setStorageInfo(response.storage);
    } catch (err) {
      console.error('Error loading storage:', err);
    }
  };

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel edit - reset to original
      setEditedData({
        username: profileData.username,
        email: profileData.email
      });
    }
    setEditMode(!editMode);
    setError('');
    setSuccess('');
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      if (!editedData.username.trim() || !editedData.email.trim()) {
        setError('Username dan email tidak boleh kosong');
        return;
      }

      const response = await authService.updateProfile(
        editedData.username,
        editedData.email
      );

      if (response.success) {
        setSuccess('Profil berhasil diperbarui!');
        setProfileData(prev => ({
          ...prev,
          username: editedData.username,
          email: editedData.email
        }));
        setEditMode(false);

        // Update user in localStorage
        const updatedUser = { ...user, ...editedData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.message || 'Gagal memperbarui profil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setError('');
      setSuccess('');

      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        setError('Semua field password harus diisi');
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setError('Password baru minimal 6 karakter');
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setError('Password baru dan konfirmasi tidak cocok');
        return;
      }

      // Mock API Call (Replace with real one)
      // await authService.changePassword(...)

      // Simulating success as backend might not be ready
      console.log("Password change requested", passwordData);

      setSuccess('Password berhasil diubah!');
      setPasswordDialogOpen(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.message || 'Gagal mengubah password');
    }
  };

  const getStoragePercentage = () => {
    if (!storageInfo) return 0;
    return (storageInfo.used / storageInfo.limit) * 100;
  };

  if (loading) {
    return (
      <SidebarLayout storageData={null}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
          <Loader2 style={{ width: '48px', height: '48px', color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout storageData={storageInfo ? { storage: storageInfo } : null}>
      <div>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <User className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
              Profil Saya
            </h1>
            <p className="text-muted-foreground">
              Kelola informasi pribadi dan pengaturan akun.
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6 border-green-500 text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-300">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertTitle>Berhasil</AlertTitle>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Edit */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xl font-bold">Informasi Akun</CardTitle>
                <Button
                  variant={editMode ? "outline" : "default"}
                  size="sm"
                  onClick={handleEditToggle}
                  disabled={saving}
                >
                  {editMode ? <><X className="h-4 w-4 mr-2" /> Batal</> : <><Edit2 className="h-4 w-4 mr-2" /> Edit Profil</>}
                </Button>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <div className="flex flex-col items-center space-y-2">
                    <Avatar className="h-24 w-24">
                      <AvatarImage src="" />
                      <AvatarFallback className="text-2xl bg-primary text-primary-foreground font-bold">
                        {profileData.username?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-center">
                      <Badge variant="secondary" className="mb-1">{profileData.role}</Badge>
                      <Badge variant={profileData.account_status === 'approved' ? "default" : "secondary"} className="bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900 dark:text-green-300">
                        {profileData.account_status}
                      </Badge>
                    </div>
                  </div>

                  <div className="flex-1 space-y-4 w-full">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Username</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            className="pl-9"
                            value={editMode ? editedData.username : profileData.username}
                            onChange={(e) => setEditedData({ ...editedData, username: e.target.value })}
                            disabled={!editMode || saving}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            className="pl-9"
                            value={editMode ? editedData.email : profileData.email}
                            onChange={(e) => setEditedData({ ...editedData, email: e.target.value })}
                            disabled={!editMode || saving}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Role</Label>
                        <div className="relative">
                          <Shield className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            className="pl-9 capitalize"
                            value={profileData.role}
                            disabled
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Member Sejak</Label>
                        <Input
                          value={new Date(profileData.created_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}
                          disabled
                        />
                      </div>
                    </div>

                    {editMode && (
                      <div className="flex justify-end pt-4">
                        <Button onClick={handleSaveProfile} disabled={saving}>
                          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Menyimpan...</> : <><Save className="h-4 w-4 mr-2" /> Simpan Perubahan</>}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Keamanan</CardTitle>
                <CardDescription>Update password dan keamanan akun Anda.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Password</h4>
                    <p className="text-sm text-muted-foreground">Ubah password Anda secara berkala untuk keamanan.</p>
                  </div>
                  <Button variant="outline" onClick={() => setPasswordDialogOpen(true)}>
                    <Lock className="h-4 w-4 mr-2" /> Ubah Password
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Stats */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-lg dark:bg-blue-900 dark:text-blue-300">
                    <HardDrive className="h-5 w-5" />
                  </div>
                  <CardTitle>Storage</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {storageInfo ? (
                  <div className="space-y-4">
                    <div className="flex items-end justify-between">
                      <div>
                        <span className="text-3xl font-bold">{formatFileSize(storageInfo.used)}</span>
                        <span className="text-sm text-muted-foreground ml-1">terpakai</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Progress value={getStoragePercentage()} className="h-2" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>0 GB</span>
                        <span>{formatFileSize(storageInfo.limit)}</span>
                      </div>
                    </div>
                    <div className="pt-2 grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-secondary/50 p-2 rounded">
                        <span className="block text-muted-foreground text-xs">Sisa</span>
                        <span className="font-semibold text-green-600">{formatFileSize(storageInfo.available)}</span>
                      </div>
                      <div className="bg-secondary/50 p-2 rounded">
                        <span className="block text-muted-foreground text-xs">Total</span>
                        <span className="font-semibold">{formatFileSize(storageInfo.limit)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="py-4 text-center text-muted-foreground">Memuat data storage...</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-green-100 text-green-600 rounded-lg dark:bg-green-900 dark:text-green-300">
                    <FileText className="h-5 w-5" />
                  </div>
                  <CardTitle>Statistik File</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-4">
                  <div className="text-4xl font-bold mb-1">{storageInfo?.files_count || 0}</div>
                  <div className="text-sm text-muted-foreground">Total File Tersimpan</div>
                </div>
              </CardContent>
              <CardFooter className="flex-col gap-2">
                <Button className="w-full" variant="secondary" onClick={() => window.location.href = '/dashboard'}>
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Ke Dashboard
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ubah Password</DialogTitle>
            <DialogDescription>
              Masukkan password saat ini dan password baru Anda.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Password Saat Ini</Label>
              <div className="relative">
                <Input
                  type={showPasswords.current ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                >
                  {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Password Baru</Label>
              <div className="relative">
                <Input
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                >
                  {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Minimal 6 karakter.</p>
            </div>
            <div className="space-y-2">
              <Label>Konfirmasi Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                >
                  {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPasswordDialogOpen(false)}>Batal</Button>
            <Button onClick={handleChangePassword}>Simpan Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarLayout>
  );
};

export default Profile;